import { useCallback, useEffect, useState } from "react";
import { ArrowLeft, Send, StickyNote, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "../../lib/api";
import { Badge, TICKET_STATUS, PRIO, SlaDot, TemplateSelect, card, inputCls, btnPrimary, btnGhost, fmtDateTime, timeAgo } from "./common";

function TicketDetail({ id, employees, onBack, me }) {
  const [data, setData] = useState(null);
  const [reply, setReply] = useState("");
  const [note, setNote] = useState("");
  const [alsoEmail, setAlsoEmail] = useState(true);
  const [busy, setBusy] = useState(false);
  const [templates, setTemplates] = useState([]);
  useEffect(() => { api.get("/admin/email-templates").then(({ data }) => setTemplates(data.templates)).catch(() => {}); }, []);
  const load = useCallback(() => api.get(`/admin/tickets/${id}`).then(({ data }) => setData(data)).catch((e) => toast.error(errText(e))), [id]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="h-60 bg-muted rounded-2xl animate-pulse" />;
  const t = data.ticket;
  const act = async (fn, ok) => { setBusy(true); try { await fn(); ok && toast.success(ok); load(); } catch (e) { toast.error(errText(e)); } finally { setBusy(false); } };
  return (
    <div className="space-y-4" data-testid="ticket-detail">
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground" data-testid="ticket-back-btn"><ArrowLeft className="w-4 h-4" /> Zurück zur Liste</button>
      <div className={`${card} p-5`}>
        <div className="flex flex-wrap items-center gap-2.5">
          <span className="font-mono text-xs font-bold bg-secondary px-2 py-1 rounded">{t.ticket_id}</span>
          <Badge map={TICKET_STATUS} value={t.status} />
          <Badge map={PRIO} value={t.priority} />
          {t.sla && (
            <span className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full ${t.sla.state === "OVERDUE" ? "bg-red-100 text-red-700" : t.sla.state === "WARN" ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"}`} data-testid="ticket-sla-chip">
              ⏱ {t.sla.hours < 1 ? "<1" : Math.round(t.sla.hours)} Std. ohne Antwort{t.sla.state === "OVERDUE" ? " – überfällig!" : ""}
            </span>
          )}
          {t.restricted && <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700">Vertraulich</span>}
          <span className="text-xs text-muted-foreground ml-auto">{fmtDateTime(t.created_at)}</span>
        </div>
        <h2 className="font-bold text-lg mt-2">{t.subject}</h2>
        <p className="text-sm text-muted-foreground">{t.name} · {t.email} {data.user && <>· Konto: <b>{data.user.username}</b> ({data.user.status})</>}</p>
        <div className="flex flex-wrap gap-2 mt-4">
          <select className="text-sm border border-border rounded-full px-3 py-2 bg-white" value={t.status} data-testid="ticket-status-select"
            onChange={(e) => act(() => api.post(`/admin/tickets/${id}/status`, { status: e.target.value }), "Status aktualisiert")}>
            {Object.entries(TICKET_STATUS).map(([k, [l]]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <select className="text-sm border border-border rounded-full px-3 py-2 bg-white" value={t.priority} data-testid="ticket-priority-select"
            onChange={(e) => act(() => api.post(`/admin/tickets/${id}/priority`, { priority: e.target.value }), "Priorität aktualisiert")}>
            {Object.entries(PRIO).map(([k, [l]]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <select className="text-sm border border-border rounded-full px-3 py-2 bg-white" value={t.assignee_id || ""} data-testid="ticket-assign-select"
            onChange={(e) => act(() => api.post(`/admin/tickets/${id}/assign`, { assignee_id: e.target.value || null }), "Zuweisung aktualisiert")}>
            <option value="">— Nicht zugewiesen —</option>
            {employees.map((emp) => <option key={emp.id} value={emp.id}>{emp.name || emp.username}</option>)}
          </select>
          {t.assignee_id !== me.id && (
            <button className={btnGhost} disabled={busy} data-testid="ticket-take-btn"
              onClick={() => act(() => api.post(`/admin/tickets/${id}/assign`, { assignee_id: me.id }), "Ticket übernommen")}>Übernehmen</button>
          )}
        </div>
      </div>

      <div className={`${card} p-5`}>
        <p className="font-bold text-sm mb-4">Verlauf</p>
        <div className="space-y-3" data-testid="ticket-thread">
          {data.messages.map((m) => (
            <div key={m.id} className={`rounded-2xl p-3.5 text-sm ${m.internal ? "bg-amber-50 border border-amber-200" : m.author_type === "STAFF" ? "bg-accent/5 border border-accent/20" : "bg-secondary/60 border border-border"}`}>
              <p className="text-xs font-bold mb-1 flex items-center gap-2">
                {m.author_name || "Kunde"}
                {m.internal && <span className="text-[10px] bg-amber-200 text-amber-900 px-1.5 py-0.5 rounded-full inline-flex items-center gap-1"><StickyNote className="w-3 h-3" /> Interne Notiz</span>}
                {m.via === "EMAIL" && <span className="text-[10px] bg-secondary px-1.5 py-0.5 rounded-full inline-flex items-center gap-1"><Mail className="w-3 h-3" /> E-Mail</span>}
                <span className="text-muted-foreground font-normal ml-auto">{timeAgo(m.created_at)}</span>
              </p>
              <p className="whitespace-pre-wrap">{m.body}</p>
            </div>
          ))}
        </div>
        <div className="mt-5 space-y-3">
          <div className="flex items-center">
            <p className="text-sm font-bold">Antwort an den Kunden</p>
            <span className="ml-auto"><TemplateSelect templates={templates} onPick={(t) => setReply(t.body)} /></span>
          </div>
          <textarea className={`${inputCls} min-h-[90px]`} placeholder="Antwort an den Kunden schreiben…" value={reply} onChange={(e) => setReply(e.target.value)} data-testid="ticket-reply-input" />
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-xs font-semibold text-muted-foreground cursor-pointer">
              <input type="checkbox" checked={alsoEmail} onChange={(e) => setAlsoEmail(e.target.checked)} className="accent-[#FF6B35]" data-testid="ticket-also-email-checkbox" /> Zusätzlich per E-Mail senden
            </label>
            <button className={`${btnPrimary} ml-auto inline-flex items-center gap-1.5`} disabled={busy || !reply.trim()} data-testid="ticket-reply-btn"
              onClick={() => act(async () => { const { data: r } = await api.post(`/admin/tickets/${id}/reply`, { body: reply, also_email: alsoEmail }); setReply(""); toast.success(r.email_sent ? "Antwort gesendet (App + E-Mail)" : "Antwort gesendet (App)"); })}>
              <Send className="w-3.5 h-3.5" /> Antworten
            </button>
          </div>
          <div className="flex gap-2">
            <input className={inputCls} placeholder="Interne Notiz (nicht für den Kunden sichtbar)…" value={note} onChange={(e) => setNote(e.target.value)} data-testid="ticket-note-input" />
            <button className={btnGhost} disabled={busy || !note.trim()} data-testid="ticket-note-btn"
              onClick={() => act(async () => { await api.post(`/admin/tickets/${id}/note`, { body: note }); setNote(""); }, "Notiz gespeichert")}>Notiz</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function PanelTickets({ me, initialSelected = null }) {
  const [res, setRes] = useState(null);
  const [filters, setFilters] = useState({ status: "", category: "", priority: "", q: "" });
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(initialSelected);
  const [employees, setEmployees] = useState([]);
  const [qInput, setQInput] = useState("");

  useEffect(() => {
    const t = setTimeout(() => { setFilters((f) => ({ ...f, q: qInput })); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [qInput]);

  useEffect(() => {
    api.get("/admin/panel/overview").then(({ data }) => setEmployees((data.per_employee || []).filter((e) => e.status === "ACTIVE"))).catch(() => {});
  }, []);
  const load = useCallback(() => {
    const p = new URLSearchParams({ page });
    Object.entries(filters).forEach(([k, v]) => v && p.set(k, v));
    api.get(`/admin/tickets?${p}`).then(({ data }) => setRes(data)).catch((e) => toast.error(errText(e)));
  }, [filters, page]);
  useEffect(() => { load(); }, [load]);

  if (selected) return <TicketDetail id={selected} employees={employees} me={me} onBack={() => { setSelected(null); load(); }} />;
  return (
    <div className="space-y-4" data-testid="panel-tickets">
      <div className="flex flex-wrap gap-2">
        <input className={`${inputCls} max-w-xs`} placeholder="Suchen (Nr., Betreff, E-Mail)…" value={qInput} data-testid="tickets-search-input"
          onChange={(e) => setQInput(e.target.value)} />
        <select className="text-sm border border-border rounded-xl px-3 py-2.5 bg-white" value={filters.status} data-testid="tickets-status-filter"
          onChange={(e) => { setFilters({ ...filters, status: e.target.value }); setPage(1); }}>
          <option value="">Alle Status</option>
          {Object.entries(TICKET_STATUS).map(([k, [l]]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <select className="text-sm border border-border rounded-xl px-3 py-2.5 bg-white" value={filters.category} data-testid="tickets-category-filter"
          onChange={(e) => { setFilters({ ...filters, category: e.target.value }); setPage(1); }}>
          <option value="">Alle Kategorien</option>
          {Object.entries(res?.categories || {}).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <select className="text-sm border border-border rounded-xl px-3 py-2.5 bg-white" value={filters.priority} data-testid="tickets-priority-filter"
          onChange={(e) => { setFilters({ ...filters, priority: e.target.value }); setPage(1); }}>
          <option value="">Alle Prioritäten</option>
          {Object.entries(PRIO).map(([k, [l]]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </div>
      <div className={`${card} overflow-hidden`}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/60 text-xs text-muted-foreground">
              <tr>{["Frist", "Nr.", "Betreff", "Kunde", "Kategorie", "Priorität", "Status", "Zugewiesen", "Aktivität"].map((h) => <th key={h} className="text-left font-semibold px-3.5 py-2.5 whitespace-nowrap">{h}</th>)}</tr>
            </thead>
            <tbody data-testid="tickets-table">
              {!res && <tr><td colSpan={9} className="px-4 py-8 text-center text-muted-foreground">Lade…</td></tr>}
              {res?.tickets.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-muted-foreground">Keine Tickets gefunden.</td></tr>}
              {res?.tickets.map((t) => (
                <tr key={t.id} className="border-t border-border/60 hover:bg-secondary/40 cursor-pointer" onClick={() => setSelected(t.id)} data-testid={`ticket-row-${t.ticket_id}`}>
                  <td className="px-3.5 py-2.5" data-testid={`ticket-sla-${t.ticket_id}`}><SlaDot sla={t.sla} /></td>
                  <td className="px-3.5 py-2.5 font-mono text-xs font-bold whitespace-nowrap">{t.ticket_id}</td>
                  <td className="px-3.5 py-2.5 max-w-[240px] truncate font-semibold">{t.subject}</td>
                  <td className="px-3.5 py-2.5 max-w-[160px] truncate">{t.name}<br /><span className="text-xs text-muted-foreground">{t.email}</span></td>
                  <td className="px-3.5 py-2.5 text-xs whitespace-nowrap">{res.categories[t.category] || t.category}</td>
                  <td className="px-3.5 py-2.5"><Badge map={PRIO} value={t.priority} /></td>
                  <td className="px-3.5 py-2.5"><Badge map={TICKET_STATUS} value={t.status} /></td>
                  <td className="px-3.5 py-2.5 text-xs whitespace-nowrap">{t.assignee_name || "—"}</td>
                  <td className="px-3.5 py-2.5 text-xs text-muted-foreground whitespace-nowrap">{timeAgo(t.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {res && res.pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-border text-sm">
            <button className={btnGhost} disabled={page <= 1} onClick={() => setPage(page - 1)}>Zurück</button>
            <span className="text-xs text-muted-foreground">Seite {res.page} von {res.pages} · {res.total} Tickets</span>
            <button className={btnGhost} disabled={page >= res.pages} onClick={() => setPage(page + 1)}>Weiter</button>
          </div>
        )}
      </div>
    </div>
  );
}
